import axios from 'axios';

import { API_BASE_URL } from '../constants/api';
import {
    firestore,
    getChatV2AuthorizationHeader,
    getPaidEndpointAppCheckToken,
} from '../firebaseHelper';
import { normalizeEarlyShowMatch } from '../utils/chatEarlyShowMatch';
import { normalizeChatV2Progress } from '../utils/chatV2ProgressUtils';
import { validatePublicChatExamplesManifest } from '../utils/publicChatExamples';

export const CHAT_V2_MAX_INPUT_CHARACTERS = 2000;

const chatV2Api = axios.create({
    baseURL: API_BASE_URL,
});

const normalizeChatV2ExploreFields = (data) => ({
    exploreNextTopics: data && Array.isArray(data.exploreNextTopics) ? data.exploreNextTopics : [],
    exploreNextSuggestions: data && Array.isArray(data.exploreNextSuggestions) ? data.exploreNextSuggestions : [],
});

export const getChatV2ErrorMessage = (value) => {
    if (!value) {
        return '';
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return '';
        }

        try {
            return getChatV2ErrorMessage(JSON.parse(trimmedValue));
        } catch {
            return trimmedValue;
        }
    }

    if (typeof value === 'object') {
        return getChatV2ErrorMessage(value.error) ||
            (typeof value.message === 'string' ? value.message.trim() : '') ||
            '';
    }

    return String(value).trim();
};

const earlyShowFields = (data, runId = data?.runId) => {
    if (data?.earlyShowMatch === undefined) return {};
    const match = normalizeEarlyShowMatch(data.earlyShowMatch);
    return { earlyShowMatch: match?.runId === runId ? match : null };
};

export const normalizeChatV2RunEnvelope = (data) => {
    if (!data || typeof data !== 'object') {
        return data;
    }

    return {
        ...data,
        ...normalizeChatV2ExploreFields(data),
        ...earlyShowFields(data),
        error: getChatV2ErrorMessage(data.error) || null,
        ...(data.progress !== undefined ? {
            progress: normalizeChatV2Progress(data.progress),
        } : {}),
    };
};

const normalizeChatV2Message = (message) => {
    if (!message || typeof message !== 'object') {
        return message;
    }

    return {
        ...message,
        ...normalizeChatV2ExploreFields(message),
        ...earlyShowFields(message),
    };
};

export const normalizeChatV2ThreadResponse = (response) => response && typeof response === 'object' ? {
    ...response,
    messages: Array.isArray(response.messages) ? response.messages.map(normalizeChatV2Message) : [],
    latestRun: response.latestRun ? normalizeChatV2RunEnvelope(response.latestRun) : null,
} : response;

export const chatV2Request = async ({
    method,
    url,
    data,
    allowAnonymous = true,
    includeAuthorization = true,
    requiresAppCheck = false,
    timeout,
    baseURL,
}) => {
    const [authorization, appCheckToken] = await Promise.all([
        includeAuthorization
            ? getChatV2AuthorizationHeader({ allowAnonymous })
            : Promise.resolve(null),
        requiresAppCheck ? getPaidEndpointAppCheckToken() : Promise.resolve(null),
    ]);
    const headers = {};
    if (authorization) {
        headers.Authorization = authorization;
    }
    if (appCheckToken) {
        headers['X-Firebase-AppCheck'] = appCheckToken;
    }
    const response = await chatV2Api({
        method,
        url,
        data,
        ...(baseURL ? { baseURL } : {}),
        headers: Object.keys(headers).length > 0 ? headers : undefined,
        ...(timeout ? { timeout } : {}),
    });

    return response.data;
};

const validatedChatV2Input = (text) => {
    const value = String(text || '');
    if (value.length > CHAT_V2_MAX_INPUT_CHARACTERS) {
        throw new Error('Messages can be up to 2,000 characters.');
    }
    return value;
};

export const startChatV2Thread = async (text) => normalizeChatV2RunEnvelope(await chatV2Request({
    method: 'post',
    url: '/chat_start',
    data: {
        text: validatedChatV2Input(text),
    },
    requiresAppCheck: true,
}));

export const sendChatV2Message = async (threadId, text) => normalizeChatV2RunEnvelope(await chatV2Request({
    method: 'post',
    url: '/chat_message',
    data: {
        threadId,
        text: validatedChatV2Input(text),
    },
    requiresAppCheck: true,
}));

export const getChatV2Thread = async (threadId) => {
    const response = await chatV2Request({
        method: 'get',
        url: `/chat_thread/${threadId}`,
    });

    return normalizeChatV2ThreadResponse(response);
};

export const getSharedChatV2Thread = async (threadId) => {
    const response = await chatV2Request({
        method: 'get',
        url: `/public_chat_thread/${threadId}`,
        allowAnonymous: false,
    });

    return normalizeChatV2ThreadResponse(response);
};

export const getPublicChatExamples = async () => validatePublicChatExamplesManifest(
    await chatV2Request({
        method: 'get',
        url: '/public_chat_examples',
        includeAuthorization: false,
        timeout: 15000,
    }),
);

export const getChatV2RunDebug = async (threadId, runId) => chatV2Request({
    method: 'get',
    url: `/debug_chatv2_run/${threadId}/${runId}`,
    allowAnonymous: false,
});

export const getChatV2Suggestions = async () => chatV2Request({
    method: 'post',
    url: '/chat_suggestions',
    data: {},
    allowAnonymous: false,
});

export const deleteChatV2Suggestion = async (suggestion) => chatV2Request({
    method: 'post',
    url: '/chat_suggestion_delete',
    data: typeof suggestion === 'string' ? {
        text: suggestion,
    } : {
        publicExampleKey: suggestion && suggestion.publicExampleKey,
        publicExamplesRevision: suggestion && suggestion.publicExamplesRevision,
    },
    allowAnonymous: false,
});

export const shareChatV2Thread = async (threadId) => chatV2Request({
    method: 'post',
    url: '/chat_thread_share',
    data: {
        threadId,
    },
});

export const unshareChatV2Thread = async (threadId) => chatV2Request({
    method: 'post',
    url: '/chat_thread_unshare',
    data: {
        threadId,
    },
});

export const deleteChatV2Thread = async (threadId) => chatV2Request({
    method: 'post',
    url: '/chat_thread_delete',
    data: {
        threadId,
    },
    allowAnonymous: false,
});

export const getChatV2RunPublicStatusRef = (threadId, runId) => firestore
    .collection('chat_threads')
    .doc(threadId)
    .collection('runs')
    .doc(runId)
    .collection('public_status')
    .doc('current');

export const normalizeChatV2Run = (runId, data) => {
    if (!runId) {
        return null;
    }

    return {
        runId,
        ...earlyShowFields(data, runId),
        ...(data?.engineVersion === 3 ? { engineVersion: 3 } : {}),
        ...(data?.previewPresentationMode === 'summary_only' ? { previewPresentationMode: 'summary_only' } : {}),
        status: data && data.status !== undefined ? data.status : null,
        stage: data && data.stage !== undefined ? data.stage : null,
        answerMode: data && data.answerMode !== undefined ? data.answerMode : null,
        statusMessage: data && data.statusMessage !== undefined ? data.statusMessage : null,
        progress: data && data.progress !== undefined ? normalizeChatV2Progress(data.progress) : null,
        assistantText: data && typeof data.assistantText === 'string' ? data.assistantText : null,
        sections: data && Array.isArray(data.sections) ? data.sections : [],
        exploreNextTopics: data && Array.isArray(data.exploreNextTopics) ? data.exploreNextTopics : [],
        exploreNextSuggestions: data && Array.isArray(data.exploreNextSuggestions) ? data.exploreNextSuggestions : [],
        actions: data && Array.isArray(data.actions) ? data.actions : [],
        error: getChatV2ErrorMessage(data && data.error) || null,
    };
};

export const isChatV2RunActive = (run) => Boolean(run && (run.status === 'queued' || run.status === 'running'));

export const isChatV2RunTerminal = (run) => Boolean(run && (run.status === 'complete' || run.status === 'needs_input' || run.status === 'failed'));
